// src/util/measurement.js
// Helpers to detect item kind (liquid/unit/weight), parse capacities, and conversions

const OZ_TO_ML = 29.5735;
const LB_TO_G = 453.59237;
const OZ_TO_G = 28.349523125;

export function getPriceFromCatalogItem(item) {
  if (!item || typeof item !== 'object') return 0;
  const p = item.price != null ? item.price : (item.pricePerBottle != null ? item.pricePerBottle : item.pricePerUnit);
  const num = parseFloat(p);
  return Number.isFinite(num) ? num : 0;
}

export function parseUnitLabel(unitLabelRaw) {
  const unitLabel = String(unitLabelRaw || '').trim().toLowerCase();
  if (!unitLabel || unitLabel === '—' || unitLabel === '-') {
    return { kind: 'unit', capacityUnits: 1, raw: unitLabel };
  }
  // 50 ct, 12ct
  const ctMatch = unitLabel.match(/(\d+(?:\.\d+)?)\s*ct/);
  if (ctMatch) {
    const capacityUnits = parseFloat(ctMatch[1]);
    return { kind: 'unit', capacityUnits: Number.isFinite(capacityUnits) ? capacityUnits : 1, raw: unitLabel };
  }
  // weight like "1 lb", "5 lb", "16 oz", "500 g", "0.5 kg"
  const weightMatch = unitLabel.match(/(\d+(?:\.\d+)?)\s*(lb|lbs|pound|pounds|oz|ounce|ounces|g|gram|grams|kg|kilogram|kilograms)/);
  if (weightMatch) {
    const amount = parseFloat(weightMatch[1]);
    const unit = weightMatch[2];
    let grams = 0;
    if (/^lb|lbs|pound/.test(unit)) grams = amount * LB_TO_G;
    else if (/^oz|ounce/.test(unit)) grams = amount * OZ_TO_G;
    else if (/^kg|kilogram/.test(unit)) grams = amount * 1000;
    else grams = amount; // g/gram/grams
    return { kind: 'weight', capacityGrams: grams, raw: unitLabel };
  }
  // Could be pieces per pack wording like "1 bunch", "10 pack" -> treat as unit
  const unitish = unitLabel.match(/(\d+(?:\.\d+)?)\s*(pack|packs|bunch|bunches|each|ea|pc|pcs|piece|pieces)/);
  if (unitish) {
    const amt = parseFloat(unitish[1]);
    return { kind: 'unit', capacityUnits: Number.isFinite(amt) ? amt : 1, raw: unitLabel };
  }
  // Fallback unknown -> treat as single unit pack
  return { kind: 'unit', capacityUnits: 1, raw: unitLabel };
}

export function detectItemKind(catalogItem) {
  if (!catalogItem || typeof catalogItem !== 'object') return { kind: 'unknown' };
  const vol = parseFloat(catalogItem.volume_ml);
  if (Number.isFinite(vol) && vol > 0) {
    return { kind: 'liquid', capacityMl: vol };
  }
  const parsed = parseUnitLabel(catalogItem.unitLabel);
  return parsed;
}

export function toMl(quantity, unit) {
  const q = parseFloat(quantity) || 0;
  switch (String(unit || '').toLowerCase()) {
    case 'ml': return q;
    case 'oz': return q * OZ_TO_ML;
    case 'liter':
    case 'l': return q * 1000;
    default: return q * OZ_TO_ML; // default assume oz
  }
}

export function toGrams(quantity, unit) {
  const q = parseFloat(quantity) || 0;
  switch (String(unit || '').toLowerCase()) {
    case 'g':
    case 'gram':
    case 'grams': return q;
    case 'kg':
    case 'kilogram':
    case 'kilograms': return q * 1000;
    case 'lb':
    case 'lbs':
    case 'pound':
    case 'pounds': return q * LB_TO_G;
    case 'oz':
    case 'ounce':
    case 'ounces': return q * OZ_TO_G;
    default: return q; // assume grams
  }
}

export const CONVERSIONS = { OZ_TO_ML, LB_TO_G, OZ_TO_G };
